/**
 * How many leads came in, where from, and how many became clients.
 *
 * The chart is arrivals against CONVERSIONS, not against wins. A lead carries no
 * `wonAt`, so the only way to date a win is the row's last edit - which any later
 * change re-dates. `convertedAt` is stamped once, when the client record is
 * created, so it is the only honest second line. Won and lost still appear in the
 * strip, where they are the current state of the leads that arrived in this
 * window rather than a claim about when the outcome happened.
 */
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { fmtMoney, fmtNum, fmtPct } from "../../ui";
import { leadSourceLabel, mergeLeadSources } from "../../../lib/leadSource";
import { Breakdown, MonthChart, Stat, StatStrip } from "./bits";
import type { LeadsReport } from "../../../lib/reports";

export function LeadsReportTab({ data }: { data: LeadsReport }) {
  const t = useT();
  const { locale } = useI18n();
  const { totals } = data;
  // Folded before anything is measured: the API counts the raw column, and two
  // spellings of one origin must not become two rows with one label.
  const sources = mergeLeadSources(data.bySource.map((r) => ({ source: r.source, count: r.count })));

  return (
    <>
      <StatStrip columns={5}>
        <Stat label={t("reports.leads.created")} value={fmtNum(totals.created)} />
        <Stat label={t("reports.leads.converted")} value={fmtNum(totals.converted)} tone="ok" />
        <Stat
          label={t("reports.leads.conversion")}
          value={totals.conversionPct === null ? "-" : fmtPct(totals.conversionPct)}
        />
        <Stat label={t("reports.leads.open")} value={fmtNum(totals.open)} sub={t("reports.leads.lostSub", { count: fmtNum(totals.lost) })} />
        <Stat label={t("reports.leads.value")} value={fmtMoney(totals.valueCents)} sub={t("reports.leads.wonValue", { v: fmtMoney(totals.wonValueCents) })} />
      </StatStrip>

      <div className="crm-rep-grid">
        <MonthChart
          title={t("reports.leads.chartTitle")}
          sub={t("reports.leads.chartSub")}
          months={data.meta.months}
          locale={locale}
          lines={[
            { label: t("reports.leads.created"), values: data.months.map((m) => m.created), color: "var(--color-primary)" },
            { label: t("reports.leads.converted"), values: data.months.map((m) => m.converted), color: "var(--color-success)" },
          ]}
        />
      </div>

      <div className="crm-rep-grid is-split">
        <Breakdown
          title={t("reports.leads.byStatus")}
          rows={data.byStatus.map((r) => ({
            key: r.status,
            label: t(`leads.status.${r.status}`),
            weight: r.count,
            value: fmtNum(r.count),
            sub: fmtMoney(r.valueCents),
          }))}
          empty={t("reports.empty")}
        />
        <Breakdown
          title={t("reports.leads.bySource")}
          rows={sources.map((r) => ({
            key: r.source,
            label: leadSourceLabel(r.source, t),
            weight: r.count,
            value: fmtNum(r.count),
          }))}
          empty={t("reports.empty")}
          color="var(--color-primary-2)"
        />
      </div>

      <div className="crm-rep-grid">
        <Breakdown
          title={t("reports.leads.byOwner")}
          sub={t("reports.leads.byOwnerSub")}
          rows={data.byOwner.map((r) => ({
            key: r.userId,
            // A colleague whose account is gone still owns the leads they took.
            label: r.name || t("reports.unknownUser"),
            weight: r.count,
            value: fmtNum(r.count),
            sub: t("reports.leads.wonSub", { count: fmtNum(r.won) }),
          }))}
          empty={t("reports.leads.noOwner")}
        />
      </div>

      <p className="crm-rep-note">{t("reports.leads.note")}</p>
    </>
  );
}
